import React, { useState, useEffect, useRef } from 'react';
import * as cache from './lib/cache';
import Navbar from './components/Navbar';
import ChatWidget from './components/ChatWidget';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import PersonalDashboard from './pages/PersonalDashboard';
import LifeFinance from './pages/LifeFinance';
import LegalGPT from './pages/LegalGPT';
import Profile from './pages/Profile';
import Welcome from './pages/Welcome';
import About from './pages/About';
import Privacy from './pages/Privacy';
import LogoutSuccess from './pages/LogoutSuccess';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
// ── The Market GPTs — imported to keep routes accessible; NOT linked from core app UI ──
import CareerCoPilot from './pages/CareerCoPilot';
import ExitEngineer from './pages/ExitEngineer';
import DecisionIntelligence from './pages/DecisionIntelligence';
import CustomerInsights from './pages/CustomerInsights';
// ── New Finance Pages ────────────────────────────────────────────────────────
import DebtIntelligence from './pages/DebtIntelligence';
import AffordabilityEngine from './pages/AffordabilityEngine';
import CashFlowReality from './pages/CashFlowReality';
import ProfessionalsDirectory from './pages/ProfessionalsDirectory';
import ConnectFlow from './pages/ConnectFlow';
import MatterStatus from './pages/MatterStatus';
import PricingPage from './pages/PricingPage';
import MarketPage from './pages/MarketPage';
import ContactPage from './pages/ContactPage';

const AxigonWebsite = () => {
  // Read cached session synchronously — prevents landing page flash for logged-in users
  const cachedSession = (() => { try { return JSON.parse(localStorage.getItem('axigon_session') || 'null'); } catch { return null; } })();
  const [currentPage, setCurrentPage] = useState(cachedSession ? 'personal-dashboard' : 'company');
  const [scrolled, setScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(!!cachedSession);
  const [user, setUser] = useState(cachedSession);
  const [financeProfile, setFinanceProfile] = useState(null);
  const [financeHealthScore, setFinanceHealthScore] = useState(null);
  const [financeScoreLoading, setFinanceScoreLoading] = useState(false);
  const [taxData, setTaxData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [taxLoading, setTaxLoading] = useState(false);
  const [financeDocuments, setFinanceDocuments] = useState([]);
  // pageHistory read value unused — goBack uses functional updater pattern
  // eslint-disable-next-line no-unused-vars
  const [pageHistory, setPageHistory] = useState([]);
  const goBackRef = useRef(null);
  const [legalFirstTime, setLegalFirstTime] = useState(false);
  const [lifeFinanceFirstTime, setLifeFinanceFirstTime] = useState(false);
  const [lifeFinanceActiveTab, setLifeFinanceActiveTab] = useState('chat');
  const [activeProfileType, setActiveProfileType] = useState(null);
  const [oauthNotice, setOauthNotice] = useState('');

  const navigateTo = (page) => {
    setPageHistory(prev => [...prev, currentPage]);
    setCurrentPage(page);
    window.history.pushState({ axigon: true }, '', window.location.pathname);
  };

  const goBack = (fallback) => {
    const defaultFallback = 'personal-dashboard';
    setPageHistory(prev => {
      if (prev.length === 0) {
        setCurrentPage(fallback ?? defaultFallback);
        return prev;
      }
      const next = [...prev];
      const target = next.pop();
      setCurrentPage(target);
      return next;
    });
  };

  goBackRef.current = goBack;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.history.pushState({ axigon: true }, '', window.location.pathname);
    const handlePopState = (e) => {
      if (!e.state?.axigon) return;
      window.history.pushState({ axigon: true }, '', window.location.pathname);
      goBackRef.current?.();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then((r) => {
        if (r.status === 401) {
          // Cookie genuinely expired — clear cached session
          localStorage.removeItem('axigon_session');
          setUser(null);
          setIsLoggedIn(false);
          setCurrentPage('company');
          return null;
        }
        return r.ok ? r.json() : null;
      })
      .then((data) => {
        if (data?.user) {
          setUser(data.user);
          setIsLoggedIn(true);
          localStorage.setItem('axigon_session', JSON.stringify(data.user));
          const pt = data.user.activeProfileType ||
            (data.user.accountType === 'personal' ? 'PERSONAL' : 'BUSINESS');
          setActiveProfileType(pt);

          // Landed here from a social (Google/Microsoft) sign-in: route into the app.
          const social = new URLSearchParams(window.location.search).get('social');
          if (social) {
            setCurrentPage(social === 'welcome'
              ? 'welcome'
              : (data.user.accountType === 'personal' ? 'personal-dashboard' : 'dashboard'));
            window.history.replaceState({}, '', '/');
          }
        }
        // Any other failure (500, network) — keep cached session, don't log out
      })
      .catch(() => {});
  }, []);

  // Surface OAuth failures (provider denied, misconfig, etc.) on the login screen.
  useEffect(() => {
    const err = new URLSearchParams(window.location.search).get('auth_error');
    if (!err) return;
    const MESSAGES = {
      provider_unavailable: 'That sign-in option is not available yet. Please use email, or try again later.',
      denied: 'Sign-in was cancelled.',
      no_email: "We couldn't get an email from that account. Please use a different method.",
      email_unverified: 'That account\'s email is not verified. Please verify it with the provider first.',
      bad_state: 'Your sign-in session expired. Please try again.',
    };
    setOauthNotice(MESSAGES[err] || 'Social sign-in failed. Please try again or use email.');
    setCurrentPage('login');
    window.history.replaceState({}, '', '/');
  }, []);

  useEffect(() => {
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/svg+xml';
    link.rel = 'icon';
    link.href = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><rect width="512" height="512" rx="112" fill="%230f3d22"/><g fill="none" stroke="%2300C27C" stroke-width="34" stroke-linecap="round" stroke-linejoin="round"><path d="M130 366 L196 150 L262 366"/><path d="M160 300 L232 300"/><path d="M300 150 L382 366"/><path d="M382 150 L300 366"/></g></svg>';
    document.head.appendChild(link);
    document.title = 'Axigon AI — Personal Finance Intelligence';
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('axigon_session');
    cache.invalidate('fin_');
    setUser(null);
    setIsLoggedIn(false);
    setPageHistory([]);
    setCurrentPage('company');
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
  };

  const fetchFinanceDocuments = async () => {
    const cached = cache.get('fin_documents');
    if (cached) { setFinanceDocuments(cached); return; }
    try {
      const res = await fetch('/api/finance?action=documents', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const docs = data.documents || [];
        cache.set('fin_documents', docs, cache.TTL.MEDIUM);
        setFinanceDocuments(docs);
      }
    } catch {}
  };

  const fetchFinanceProfile = async () => {
    const cached = cache.get('fin_profile');
    if (cached !== null) { setFinanceProfile(cached); return; }
    try {
      const res = await fetch('/api/finance?action=profile', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const profile = data.profile || null;
        cache.set('fin_profile', profile, cache.TTL.MEDIUM);
        setFinanceProfile(profile);
      }
    } catch {}
  };

  const fetchHealthScore = async () => {
    const cached = cache.get('fin_health_score');
    if (cached) { setFinanceHealthScore(cached); return; }
    setFinanceScoreLoading(true);
    try {
      const res = await fetch('/api/finance?action=health-score', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        cache.set('fin_health_score', data, cache.TTL.MEDIUM);
        setFinanceHealthScore(data);
      }
    } catch {}
    setFinanceScoreLoading(false);
  };

  const fetchTaxEstimate = async () => {
    const cached = cache.get('fin_tax_estimate');
    if (cached) { setTaxData(cached); return; }
    setTaxLoading(true);
    try {
      const r = await fetch('/api/finance?action=tax-estimate', { credentials: 'include' });
      const d = await r.json();
      const result = d.error ? null : d;
      if (result) cache.set('fin_tax_estimate', result, cache.TTL.LONG);
      setTaxData(result);
    } catch {}
    setTaxLoading(false);
  };

  const clearFirstLogin = async () => {
    try {
      await fetch('/api/users/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isFirstLogin: false }),
      });
    } catch {}
    setUser(prev => prev ? { ...prev, isFirstLogin: false } : prev);
  };

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif", backgroundColor: 'var(--bg-page)' }}>

      <Navbar
        user={user}
        isLoggedIn={isLoggedIn}
        scrolled={scrolled}
        currentPage={currentPage}
        navigateTo={navigateTo}
        handleLogout={handleLogout}
        onOpenProfile={() => navigateTo('profile')}
        activeProfileType={activeProfileType}
      />

      {currentPage === 'company' && (
        <LandingPage user={user} isLoggedIn={isLoggedIn} navigateTo={navigateTo} setUser={setUser} setIsLoggedIn={setIsLoggedIn} />
      )}

      {currentPage === 'dashboard' && (
        <Dashboard
          navigateTo={navigateTo}
          setLegalFirstTime={setLegalFirstTime}
          setLifeFinanceFirstTime={setLifeFinanceFirstTime}
          setLifeFinanceActiveTab={setLifeFinanceActiveTab}
        />
      )}

      {currentPage === 'personal-dashboard' && (
        <PersonalDashboard
          user={user}
          navigateTo={navigateTo}
          setLegalFirstTime={setLegalFirstTime}
          setLifeFinanceFirstTime={setLifeFinanceFirstTime}
          setLifeFinanceActiveTab={setLifeFinanceActiveTab}
        />
      )}

      {currentPage === 'life-finance' && (
        <LifeFinance
          user={user}
          navigateTo={navigateTo}
          goBack={goBack}
          financeProfile={financeProfile}
          setFinanceProfile={setFinanceProfile}
          financeDocuments={financeDocuments}
          setFinanceDocuments={setFinanceDocuments}
          financeHealthScore={financeHealthScore}
          financeScoreLoading={financeScoreLoading}
          fetchFinanceProfile={fetchFinanceProfile}
          fetchFinanceDocuments={fetchFinanceDocuments}
          fetchHealthScore={fetchHealthScore}
          fetchTaxEstimate={fetchTaxEstimate}
          taxData={taxData}
          lifeFinanceFirstTime={lifeFinanceFirstTime}
          setLifeFinanceFirstTime={setLifeFinanceFirstTime}
          initialTab={lifeFinanceActiveTab}
          onTabConsumed={() => setLifeFinanceActiveTab('chat')}
        />
      )}

      {currentPage === 'legal-gpt' && (
        <LegalGPT
          user={user}
          navigateTo={navigateTo}
          goBack={goBack}
          legalFirstTime={legalFirstTime}
          setLegalFirstTime={setLegalFirstTime}
        />
      )}

      {currentPage === 'welcome' && (
        <Welcome
          user={user}
          navigateTo={navigateTo}
          clearFirstLogin={clearFirstLogin}
          setLegalFirstTime={setLegalFirstTime}
          setLifeFinanceFirstTime={setLifeFinanceFirstTime}
        />
      )}

      {currentPage === 'about' && (
        <About goBack={goBack} navigateTo={navigateTo} />
      )}

      {currentPage === 'privacy' && (
        <Privacy goBack={goBack} />
      )}

      {currentPage === 'logout-success' && (
        <LogoutSuccess navigateTo={navigateTo} />
      )}

      {currentPage === 'marketplace' && (
        // Legacy route: redirect to new 'market' page
        <MarketPage navigateTo={navigateTo} goBack={goBack} user={user} />
      )}

      {currentPage === 'login' && (
        <LoginPage
          navigateTo={navigateTo}
          setUser={setUser}
          setIsLoggedIn={setIsLoggedIn}
          notice={oauthNotice}
        />
      )}

      {currentPage === 'signup' && (
        <SignupPage
          navigateTo={navigateTo}
          setUser={setUser}
          setIsLoggedIn={setIsLoggedIn}
        />
      )}

      {currentPage === 'profile' && (
        <Profile
          user={user}
          setUser={setUser}
          navigateTo={navigateTo}
          goBack={goBack}
        />
      )}

      {/* ── New finance pages ── */}
      {currentPage === 'debt-intelligence' && (
        <DebtIntelligence user={user} navigateTo={navigateTo} goBack={goBack} />
      )}

      {currentPage === 'affordability-engine' && (
        <AffordabilityEngine user={user} navigateTo={navigateTo} goBack={goBack} />
      )}

      {currentPage === 'cashflow-reality' && (
        <CashFlowReality user={user} navigateTo={navigateTo} goBack={goBack} />
      )}


      {currentPage === 'career-co-pilot' && (
        <CareerCoPilot navigateTo={navigateTo} goBack={goBack} />
      )}

      {currentPage === 'exit-engineer' && (
        <ExitEngineer navigateTo={navigateTo} goBack={goBack} />
      )}

      {currentPage === 'decision-intelligence' && (
        <DecisionIntelligence navigateTo={navigateTo} goBack={goBack} />
      )}

      {currentPage === 'customer-insights' && (
        <CustomerInsights navigateTo={navigateTo} goBack={goBack} />
      )}

      {currentPage === 'professionals-directory' && (
        <ProfessionalsDirectory
          user={user}
          navigateTo={navigateTo}
          goBack={goBack}
          activeProfileType={activeProfileType}
        />
      )}

      {currentPage === 'connect-flow' && (
        <ConnectFlow
          user={user}
          navigateTo={navigateTo}
          goBack={goBack}
          activeProfileType={activeProfileType}
        />
      )}

      {currentPage === 'matter-status' && (
        <MatterStatus
          user={user}
          navigateTo={navigateTo}
          goBack={goBack}
          activeProfileType={activeProfileType}
        />
      )}

      {currentPage === 'pricing' && (
        <PricingPage navigateTo={navigateTo} user={user} />
      )}

      {currentPage === 'market' && (
        <MarketPage navigateTo={navigateTo} goBack={goBack} user={user} />
      )}

      {currentPage === 'contact' && (
        <ContactPage navigateTo={navigateTo} goBack={goBack} />
      )}

      {/* ── Global floating chat widget ── */}
      <ChatWidget isLoggedIn={isLoggedIn} navigateTo={navigateTo} />
    </div>
  );
};

export default AxigonWebsite;
